import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormAutosuggest, useZionFormContext, FormPlaceSuggest, FormDateSuggest, useWatch } from '@fs/zion-form'
import { ContentDelete } from '@fs/zion-icon'
import { Cell, FlowGrid, IconButton, LabelSpacer, Row, Button, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext, getNestedOrFloatingElementById } from '@fs/zion-sls'
import SubElements from './SubElements'
import OtherEventFields from './OtherEventFields'
import RelationshipEventFields from './RelationshipEventFields'
import ConfirmUnlinkEvent from './ConfirmUnlinkEvent'
import { populateExistingEventFormValues, createEmptyEvent, getEventDefaults } from '../utils/events.utils'
import SharedEventPanel from '../../panels/sharedEvent/SharedEventPanel'

const EventForm = ({
  field,
  index,
  fieldArrayMethods,
  eventAutosuggestOptions,
  person,
  appName,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()
  const { setValue, getValues, trigger, resetField } = useZionFormContext()
  const slsState = useSlsStateContext()
  const [showSharedEvent, setShowSharedEvent] = useState(false)
  const [sharedEventFormState, setSharedEventFormState] = useState(null)
  const [personToUnlink, setPersonToUnlink] = useState(null)
  const confirmUnlinkOverlay = useOverlay()

  const fieldNamePrefix = field?.primary ? 'events.primary' : 'events.additional'
  const eventTypeFieldName = `${fieldNamePrefix}[${index}].eventType`
  const basePath = `${fieldNamePrefix}[${index}]`

  const eventTypeField = useWatch({ name: eventTypeFieldName })
  const showRelationshipFields = eventTypeField?.isRelationshipEvent
  const isPersistedRelationshipEvent = field?.relationshipId

  // Watch for existing event ID and superElements from form data
  const existingEventId = useWatch({ name: `${basePath}.existingEventId` })
  const superElements = useWatch({ name: `${basePath}.superElements` })

  // Watch the actual form values for dates, places, and other to ensure we render current state
  // This is necessary because `field` from useFieldArray may be stale after resetField
  const watchedDates = useWatch({ name: `${basePath}.dates` }) || field.dates
  const watchedPlaces = useWatch({ name: `${basePath}.places` }) || field.places
  const watchedOther = useWatch({ name: `${basePath}.other` }) || field.other

  // Derive the selected event based on existingEventId
  const selectedEvent = useMemo(() => {
    if (!existingEventId) return null

    // Try to find the event in the SLS state
    return getNestedOrFloatingElementById(slsState.images, existingEventId)
  }, [existingEventId, slsState.images])

  // Check if this is a linked event (has superElements with more than 1 person)
  const isLinkedEvent = superElements?.length > 1

  const onSaveSharedEvent = useCallback(
    (dispatches, formState) => {
      setShowSharedEvent(false)
      setSharedEventFormState(formState)
      // Reset unlink and addLink state for this event, but keep existing confirmed actions for other events
      const currentConfirmedUnlinks = (getValues(`${basePath}.confirmedUnlinks`) || []).filter(
        (unlink) => unlink.event.id !== selectedEvent.id
      )
      const currentConfirmedAddLinks = (getValues(`${basePath}.confirmedAddLinks`) || []).filter(
        (addLink) => addLink.event.id !== selectedEvent.id
      )

      dispatches.forEach((disp) => {
        if (disp.type === 'ADD_SUB') {
          currentConfirmedAddLinks.push({
            person: getNestedOrFloatingElementById(slsState.images, disp.value.superId),
            event: selectedEvent,
          })
        } else if (disp.type === 'REM_SUB') {
          currentConfirmedUnlinks.push({
            person: getNestedOrFloatingElementById(slsState.images, disp.value.superId),
            event: selectedEvent,
          })
        }
      })
      setValue(`${basePath}.confirmedAddLinks`, [...currentConfirmedAddLinks], { shouldDirty: true })
      setValue(`${basePath}.confirmedUnlinks`, [...currentConfirmedUnlinks], { shouldDirty: true })
    },
    [basePath, selectedEvent, setValue, getValues, slsState.images]
  )

  return (
    <>
      <FlowGrid
        orientation="vertical"
        columnCount={1}
        gutters="nano"
        key={`event-${field.id}-${existingEventId || 'new'}`}
      >
        <Row gutters="xxs">
          <FormAutosuggest
            name={eventTypeFieldName}
            label={t('edit-events.fields.event-type', 'Event Type')}
            data-testid="event-type-autosuggest"
            suggestions={eventAutosuggestOptions}
            onSuggestionSelected={(val) => {
              const selectedSuggestion = val

              // Check if an existing event was selected
              if (selectedSuggestion?.isExistingEvent && selectedSuggestion?.existingEvent) {
                const event = selectedSuggestion.existingEvent

                // Populate form values from the existing event
                const { dates, places, other } = populateExistingEventFormValues(event, slsState.types)

                // Start with clean defaults to avoid ghost fields, then populate with event data
                const newEventData = {
                  ...getEventDefaults(field.primary),
                  dates,
                  places,
                  other,
                  id: event.id,
                  eventType: selectedSuggestion,
                  existingEventId: event.id,
                  superElements: event.superElements || [],
                }

                // Reset field with new event data. This both updates the form values and clears
                // "touched" flags to prevent warning messages from persisting between event switches.
                resetField(basePath, {
                  defaultValue: newEventData,
                  keepError: false,
                  keepDirty: false,
                  keepTouched: false,
                })
              } else {
                // Standard event type selected (new event)
                // Start with a clean empty event structure to avoid ghost fields from previous selections
                const emptyEventData = {
                  ...createEmptyEvent(field.primary, slsState.types),
                  eventType: val,
                }

                // Reset field with empty event structure. This both updates the form values and
                // clears "touched" flags to prevent warning messages from persisting.
                resetField(basePath, {
                  defaultValue: emptyEventData,
                  keepError: false,
                  keepDirty: false,
                  keepTouched: false,
                })
              }
            }}
            clearOnClickAway
            clearable
            growX
            dense
            // Disable to prevent editing of persisted relationship events. We'll look at a better UX later.
            // This is current behavior from RII.
            disabled={isPersistedRelationshipEvent}
            // Virtualization is disabled due to issues with grouped dropdown rendering.
          />
          {!field.primary && (
            <Cell>
              <LabelSpacer />
              <IconButton
                Icon={ContentDelete}
                title={t('edit-events.remove-event', 'Remove Event')}
                onClick={() => {
                  if (isLinkedEvent) {
                    setPersonToUnlink(person)
                    confirmUnlinkOverlay.open()
                    return
                  }
                  fieldArrayMethods.remove(index)
                  trigger() // Re-run validation after removal
                }}
                color="red"
                data-testid={`unlink-event-button-${index}`}
                linkName="Events Card Unlink Event"
              />
            </Cell>
          )}
        </Row>
        {/* Show View Linked Names button for events with multiple linked persons */}
        {isLinkedEvent && (
          <Button
            data-testid={`event-${index}-view-linked-names-button`}
            linkName="Events View Linked Names"
            onClick={() => {
              setShowSharedEvent(true)
            }}
          >
            {t('edit-events.view-linked-names', 'Manage Linked Names on Event')}
          </Button>
        )}
        {watchedDates.map((date, dateIndex) => (
          <SubElements
            key={`date-${date.id}`}
            subElements={date.subElements}
            eventIndex={index}
            itemIndex={dateIndex}
            indexName="dates"
            field={field}
            t={t}
            SuggestComponent={FormDateSuggest}
            suggestFieldType="DATE"
            suggestLabelKey="edit-events.fields.date"
            suggestDefaultLabel="Date"
            isLinkedEvent={isLinkedEvent}
            primaryEvent={field.primary}
            splitScreenImageArk={splitScreenImageArk}
          />
        ))}
        {watchedPlaces?.map((place, placeIndex) => (
          <SubElements
            key={`place-${place.id}`}
            subElements={place.subElements}
            eventIndex={index}
            itemIndex={placeIndex}
            indexName="places"
            field={field}
            t={t}
            SuggestComponent={FormPlaceSuggest}
            suggestFieldType="PLACE"
            suggestLabelKey="edit-events.fields.place"
            suggestDefaultLabel="Place"
            isLinkedEvent={isLinkedEvent}
            primaryEvent={field.primary}
            splitScreenImageArk={splitScreenImageArk}
          />
        ))}
        {watchedOther?.map((other, otherIndex) => (
          <OtherEventFields
            key={`other-${other.id}`}
            otherField={other}
            itemIndex={otherIndex}
            eventIndex={index}
            primaryEvent={field.primary}
            isLinkedEvent={isLinkedEvent}
            splitScreenImageArk={splitScreenImageArk}
          />
        ))}
        {showRelationshipFields && (
          <RelationshipEventFields person={person} index={index} fieldNamePrefix={fieldNamePrefix} />
        )}
      </FlowGrid>

      {/* Overlays moved outside FlowGrid to prevent empty cell spacing issues */}
      {/* Confirm Unlink Dialog */}
      <ConfirmUnlinkEvent
        overlay={confirmUnlinkOverlay}
        person={personToUnlink}
        event={selectedEvent}
        onRemove={() => {
          if (!personToUnlink || !selectedEvent) return

          // Close dialog immediately (consistent with ConfirmUnlinkName pattern)
          confirmUnlinkOverlay.close()

          // If personToUnlink is the focus person, remove the entire event
          // Otherwise, just unlink that specific person from the event
          if (personToUnlink?.id === person?.id) {
            // Remove the entire event from the form
            fieldArrayMethods.remove(index)
            trigger() // Re-run validation after removal
            setPersonToUnlink(null)
          } else {
            // Unlink the person from the event
            // Add to confirmedUnlinks in form state for processing on form submission
            const currentConfirmedUnlinks = getValues(`${basePath}.confirmedUnlinks`) || []
            setValue(
              `${basePath}.confirmedUnlinks`,
              [...currentConfirmedUnlinks, { person: personToUnlink, event: selectedEvent }],
              { shouldDirty: true }
            )

            // Update form state to remove the person from superElements (visual feedback)
            const currentSuperElements = superElements || []
            const updatedSuperElements = currentSuperElements.filter((supEl) => supEl.id !== personToUnlink?.id)
            setValue(`${basePath}.superElements`, updatedSuperElements, { shouldDirty: true })
            setPersonToUnlink(null)
          }
        }}
      />
      {showSharedEvent && (
        <SharedEventPanel
          event={selectedEvent}
          selectedPerson={person}
          onSave={onSaveSharedEvent}
          defaultFormState={sharedEventFormState}
          onCancel={() => setShowSharedEvent(false)}
          shouldSaveToSls={false}
          appName={appName}
        />
      )}
    </>
  )
}

EventForm.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    primary: PropTypes.bool.isRequired,
    dates: PropTypes.arrayOf(PropTypes.shape({})),
    places: PropTypes.arrayOf(PropTypes.shape({})),
    other: PropTypes.arrayOf(PropTypes.shape({})),
    eventType: PropTypes.shape({
      key: PropTypes.string,
      primaryText: PropTypes.string,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  fieldArrayMethods: PropTypes.shape({
    remove: PropTypes.func,
    update: PropTypes.func,
  }).isRequired,
  eventAutosuggestOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      primaryText: PropTypes.string,
    })
  ).isRequired,
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recordId: PropTypes.string,
    partitionKey: PropTypes.string,
  }),
}

export default EventForm
